import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronRight, ChevronDown, ArrowRight, Monitor, Smartphone, Palette, PenTool, TrendingUp, ShoppingCart, Store, LayoutTemplate, Code2, Database, ShieldCheck, Cloud, HeartPulse, GraduationCap, Building2, Truck, Plane, Home, Factory, Coffee, Cpu, Car, Zap, Type, Link as LinkIcon, Languages, FileImage, Sparkles, Eraser, Keyboard, Lock, Globe, Search, QrCode, FileStack } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
  const [isMoreUtilityOpen, setIsMoreUtilityOpen] = useState(false);
  const [expandedMobileMoreUtility, setExpandedMobileMoreUtility] = useState(false);
  const [submenuSide, setSubmenuSide] = useState<'right' | 'left'>('right');

  const megaMenuRef = useRef<HTMLDivElement | null>(null);
  const moreUtilityTriggerRef = useRef<HTMLButtonElement | null>(null);
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const moreUtilityCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const location = useLocation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const isAboutPage = location.pathname === '/about-us' || location.pathname === '/about-us/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    setExpandedMobileSection(null);
    setIsMoreUtilityOpen(false);
    setExpandedMobileMoreUtility(false);
  }, [location.pathname]);

  // Close submenu if parent dropdown changes
  useEffect(() => {
    if (activeDropdown !== 'tools') {
      setIsMoreUtilityOpen(false);
    }
  }, [activeDropdown]);

  // Dynamically position submenu so it never overflows the viewport
  const updateSubmenuPosition = () => {
    if (megaMenuRef.current) {
      const rect = megaMenuRef.current.getBoundingClientRect();
      const submenuWidth = 280;
      const gap = 12;
      const padding = 16;
      const needed = submenuWidth + gap + padding;
      const spaceOnRight = window.innerWidth - rect.right;
      const spaceOnLeft = rect.left;

      if (spaceOnRight >= needed) {
        setSubmenuSide('right');
      } else if (spaceOnLeft >= needed) {
        setSubmenuSide('left');
      } else {
        setSubmenuSide(spaceOnRight >= spaceOnLeft ? 'right' : 'left');
      }
    }
  };

  useEffect(() => {
    if (isMoreUtilityOpen || activeDropdown === 'tools') {
      updateSubmenuPosition();
    }
  }, [isMoreUtilityOpen, activeDropdown]);

  useEffect(() => {
    const handleResize = () => {
      if (isMoreUtilityOpen) {
        updateSubmenuPosition();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMoreUtilityOpen]);

  const handleMoreMouseEnter = () => {
    if (moreUtilityCloseTimer.current) {
      clearTimeout(moreUtilityCloseTimer.current);
      moreUtilityCloseTimer.current = null;
    }
    updateSubmenuPosition();
    setIsMoreUtilityOpen(true);
  };

  const handleMoreMouseLeave = () => {
    if (moreUtilityCloseTimer.current) {
      clearTimeout(moreUtilityCloseTimer.current);
    }
    moreUtilityCloseTimer.current = setTimeout(() => {
      setIsMoreUtilityOpen(false);
    }, 280);
  };

  // Auto-close dropdown on Escape key or clicking outside globally
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isMoreUtilityOpen) {
          setIsMoreUtilityOpen(false);
          moreUtilityTriggerRef.current?.focus();
        } else {
          setActiveDropdown(null);
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const header = document.querySelector('.site-header');
      if (header && !header.contains(e.target as Node)) {
        setActiveDropdown(null);
        setIsMoreUtilityOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreUtilityOpen]);

  const toggleMobileSection = (section: string) => {
    setExpandedMobileSection(expandedMobileSection === section ? null : section);
  };

  const servicesMenu = {
    core: [
      { name: 'Web Development', desc: 'Custom websites & web applications', icon: <Monitor className="w-5 h-5 text-brand-600" />, path: '/services/web-development', bgColor: 'bg-brand-100' },
      { name: 'Mobile Apps', desc: 'iOS & Android applications', icon: <Smartphone className="w-5 h-5 text-emerald-600" />, path: '/services/mobile-app', bgColor: 'bg-emerald-100' },
    ],
    design: [
      { name: 'UI/UX Design', desc: 'User experience & interface design', icon: <Palette className="w-5 h-5 text-pink-600" />, path: '/services/uiux-design', bgColor: 'bg-pink-100' },
      { name: 'Logo & Brand', desc: 'Brand identity & logo design', icon: <PenTool className="w-5 h-5 text-amber-600" />, path: '/services/logo-brand-design', bgColor: 'bg-amber-100' },
      { name: 'Digital Marketing', desc: 'SEO, PPC & social media', icon: <TrendingUp className="w-5 h-5 text-brand-600" />, path: '/services/digital-marketing', bgColor: 'bg-brand-100' },
    ],
    ecommerce: [
      { name: 'E-commerce', desc: 'Online stores & marketplaces', icon: <ShoppingCart className="w-5 h-5 text-yellow-600" />, path: '/services/ecommerce-development', bgColor: 'bg-yellow-100' },
      { name: 'Shopify', desc: 'Shopify store development', icon: <Store className="w-5 h-5 text-green-600" />, path: '/services/shopify-development', bgColor: 'bg-green-100' },
      { name: 'WordPress', desc: 'CMS & blog development', icon: <LayoutTemplate className="w-5 h-5 text-indigo-600" />, path: '/services/wordpress-development', bgColor: 'bg-indigo-100' },
    ],
    bottom: [
      { name: 'React', icon: <Code2 className="w-5 h-5 text-brand-500" />, path: '/services/react-development' },
      { name: 'Laravel', icon: <Database className="w-5 h-5 text-red-500" />, path: '/services/laravel-development' },
      { name: 'QA Testing', icon: <ShieldCheck className="w-5 h-5 text-brand-500" />, path: '/services/testing-services' },
      { name: 'Cloud DevOps', icon: <Cloud className="w-5 h-5 text-sky-500" />, path: '/services/cloud-devops' },
    ]
  };

  const industriesMenu = [
    { name: 'Healthcare', desc: 'Medical & Health Tech', icon: <HeartPulse className="w-5 h-5 text-rose-500" />, path: '/industries/healthcare-medical', bgColor: 'bg-rose-100' },
    { name: 'Education', desc: 'E-learning & EdTech', icon: <GraduationCap className="w-5 h-5 text-brand-500" />, path: '/industries/education-elearning', bgColor: 'bg-brand-100' },
    { name: 'Finance', desc: 'Banking & FinTech', icon: <Building2 className="w-5 h-5 text-emerald-500" />, path: '/industries/finance-banking', bgColor: 'bg-emerald-100' },
    { name: 'E-commerce', desc: 'Retail & Online Stores', icon: <ShoppingCart className="w-5 h-5 text-amber-500" />, path: '/industries/ecommerce-retail', bgColor: 'bg-amber-100' },
    { name: 'Logistics', desc: 'Supply Chain & Transport', icon: <Truck className="w-5 h-5 text-brand-500" />, path: '/industries/logistics-supply-chain', bgColor: 'bg-brand-100' },
    { name: 'Travel', desc: 'Tourism & Hospitality', icon: <Plane className="w-5 h-5 text-brand-500" />, path: '/industries/travel-hospitality', bgColor: 'bg-brand-100' },
    { name: 'Real Estate', desc: 'Property Management', icon: <Home className="w-5 h-5 text-purple-500" />, path: '/industries/real-estate-property', bgColor: 'bg-purple-100' },
    { name: 'Manufacturing', desc: 'Industrial Solutions', icon: <Factory className="w-5 h-5 text-slate-500" />, path: '/industries/manufacturing-industrial', bgColor: 'bg-slate-200' },
    { name: 'Food & Beverage', desc: 'Restaurant & F&B Tech', icon: <Coffee className="w-5 h-5 text-orange-500" />, path: '/industries/food-beverage', bgColor: 'bg-orange-100' },
    { name: 'Technology', desc: 'Software & SaaS', icon: <Cpu className="w-5 h-5 text-indigo-500" />, path: '/industries/technology-software', bgColor: 'bg-indigo-100' },
    { name: 'Automotive', desc: 'Transportation & Mobility', icon: <Car className="w-5 h-5 text-slate-600" />, path: '/industries/automotive-transportation', bgColor: 'bg-slate-200' },
    { name: 'Energy', desc: 'Utilities & Power', icon: <Zap className="w-5 h-5 text-yellow-500" />, path: '/industries/energy-utilities', bgColor: 'bg-yellow-100' },
  ];

  const toolsMenu = {
    developer: [
      { name: 'JSON Formatter', desc: 'Beautify & validate JSON', icon: <Code2 className="w-5 h-5 text-brand-400" />, path: '/tools/json-formatter' },
      { name: 'Case Converter', desc: 'Text transformation tools', icon: <Type className="w-5 h-5 text-emerald-400" />, path: '/tools/case-converter' },
      { name: 'Prompt to URL', desc: 'Smart AI link generator', icon: <LinkIcon className="w-5 h-5 text-brand-400" />, path: '/tools/prompt-to-url' },
      { name: 'Bcrypt Generator', desc: 'Secure hash generators', icon: <ShieldCheck className="w-5 h-5 text-purple-400" />, path: '/tools/bcrypt-generator' },
    ],
    image: [
      { name: 'Image Compressor', desc: 'Optimize image file sizes', icon: <FileImage className="w-5 h-5 text-brand-400" />, path: '/tools/image-compressor' },
      { name: 'Image Enhancer', desc: 'Browser-side scaling tools', icon: <Sparkles className="w-5 h-5 text-amber-400" />, path: '/tools/image-enhancer' },
      { name: 'Background Remover', desc: 'Remove backgrounds with AI', icon: <Eraser className="w-5 h-5 text-purple-400" />, path: '/tools/background-remover' },
    ],
    utility: [
      { name: 'Typing Test', desc: 'WPM & accuracy metrics', icon: <Keyboard className="w-5 h-5 text-brand-400" />, path: '/tools/typing-test' },
      { name: 'Color Generator', desc: 'Hex & RGB generation', icon: <Palette className="w-5 h-5 text-pink-400" />, path: '/tools/color-generator' },
      { name: 'Password Generator', desc: 'Secure random strings', icon: <Lock className="w-5 h-5 text-brand-400" />, path: '/tools/password-generator' },
      { name: 'What is my IP?', desc: 'Public IP metrics', icon: <Globe className="w-5 h-5 text-emerald-400" />, path: '/tools/what-is-my-ip' },
      { name: 'Live Translate', desc: 'Real-time translation', icon: <Languages className="w-5 h-5 text-brand-400" />, path: '/tools/live-translate' },
      { name: 'QR Code Generator', desc: 'Custom QR code maker', icon: <QrCode className="w-5 h-5 text-brand-400" />, path: '/tools/qr-code-generator' },
      { name: 'Merge PDF', desc: 'Combine PDF documents', icon: <FileStack className="w-5 h-5 text-rose-400" />, path: '/tools/merge-pdf' },
    ]
  };

  const PRIMARY_UTILITY_LIMIT = 5;
  const primaryUtilityTools = toolsMenu.utility.slice(0, PRIMARY_UTILITY_LIMIT);
  const moreUtilityTools = toolsMenu.utility.slice(PRIMARY_UTILITY_LIMIT);

  return (
    <>
      {/* Global Background Blur Overlay for Navbar Dropdowns */}
      <AnimatePresence>
        {activeDropdown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "fixed inset-0 z-[90] pointer-events-auto transition-opacity",
              isLight ? "bg-slate-900/10" : "bg-black/30"
            )}
            style={{
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)'
            }}
            onClick={() => setActiveDropdown(null)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <header
      className={cn(
        "site-header fixed z-[100] transition-all duration-500",
        "top-0 left-0 w-full lg:top-4 lg:left-1/2 lg:-translate-x-1/2 lg:w-[calc(100%-3rem)] lg:max-w-[110rem] lg:rounded-full",
        scrolled
          ? "site-header--scrolled py-2.5 shadow-2xl"
          : "py-3.5",
        isLight
          ? "bg-white/80 backdrop-blur-xl border-b lg:border border-slate-200/80 shadow-[0_8px_30px_rgba(0,0,0,0.05)]"
          : "bg-[rgba(5,5,5,0.92)] backdrop-blur-[20px] border-b lg:border border-white/[0.12] shadow-[0_15px_35px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(16,185,129,0.06)]"
      )}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center z-50 shrink-0">
            <div className="relative transition-transform hover:scale-105">
              <img
                src={isLight ? "/assets/images/logo.webp?v=1.2" : "/assets/images/white-logo.png"}
                alt="Prince IT"
                className="h-8 md:h-10 w-auto transition-all"
                referrerPolicy="no-referrer"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-3.5">
            {[
              { name: 'Home', path: '/' },
              { name: 'Revenue', path: '/revenue', isRevenue: true },
              { name: 'Services', path: '/services', isDropdown: true, dropdownId: 'services' },
              { name: 'Industries', path: '/industries', isDropdown: true, dropdownId: 'industries' },
              { name: 'Tools', path: '/tools', isDropdown: true, dropdownId: 'tools' },
              { name: 'Portfolio', path: '/portfolio' },
              { name: 'Blog', path: '/blog' },
              { name: 'About', path: '/about-us' }
            ].map((item: any) => {
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

              const NavLink = ({ children, className }: any) => {
                if (item.isExternal) {
                  return (
                    <a href={item.path} target="_blank" rel="noopener noreferrer" className={className}>
                      {children}
                    </a>
                  );
                }
                return (
                  <Link
                    to={item.path}
                    onClick={(e) => {
                      if (item.isDropdown && window.innerWidth >= 1024) {
                        if (activeDropdown !== item.dropdownId) {
                          e.preventDefault();
                          setActiveDropdown(item.dropdownId);
                        }
                      } else {
                        setActiveDropdown(null);
                      }
                    }}
                    className={className}
                  >
                    {children}
                  </Link>
                );
              };

              return (
                <div
                  key={item.name}
                  className="relative px-3 py-2 group"
                  onMouseEnter={() => item.isDropdown && setActiveDropdown(item.dropdownId as string)}
                  onMouseLeave={() => item.isDropdown && setActiveDropdown(null)}
                  onFocus={() => item.isDropdown && setActiveDropdown(item.dropdownId as string)}
                >
                  {/* Floating "New!" Badge on the Right Side above Revenue with Gold Highlight */}
                  {item.isRevenue && (
                    <div className="absolute -top-3.5 -right-1.5 pointer-events-none z-20 flex flex-col items-center">
                      <span
                        style={{ color: '#ffffff', background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}
                        className="inline-flex items-center justify-center px-2 py-[2px] rounded-full text-[9px] font-black !text-white tracking-wider shadow-[0_2px_12px_rgba(245,158,11,0.65)] leading-none whitespace-nowrap select-none border border-yellow-200/80 drop-shadow-sm"
                      >
                        New!
                      </span>
                      {/* Tiny Arrow pointer in bright gold */}
                      <span
                        style={{ borderTopColor: '#F59E0B' }}
                        className="w-0 h-0 border-x-[3px] border-x-transparent border-t-[3px] -mt-[0.5px]"
                      />
                    </div>
                  )}

                  <NavLink
                    className={cn(
                      "text-sm transition-all duration-300 flex items-center gap-1",
                      item.isRevenue
                        ? "text-brand-600 font-extrabold"
                        : (isActive
                          ? (isLight ? "text-brand-600 font-bold scale-105" : "text-brand-400 font-bold scale-105")
                          : (isLight ? "text-slate-700 hover:text-brand-600 font-bold" : "text-slate-300 hover:text-white font-bold"))
                    )}
                  >
                    {item.name}
                    {item.isDropdown && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === item.dropdownId ? 'rotate-180' : ''}`} />
                    )}
                  </NavLink>

                  {/* Active Underline (Sliding for standard routes) */}
                  {!item.isRevenue && isActive && (
                    <motion.div
                      layoutId="activeUnderline"
                      className="absolute bottom-0 left-3 right-3 h-[3px] bg-gradient-to-r from-brand-400 to-brand-400 rounded-full shadow-[0_0_12px_rgba(var(--app-primary-rgb),0.6)]"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}

                  {/* Solid Blue Underline for Revenue */}
                  {item.isRevenue && (
                    <div className="absolute bottom-0 left-3 right-3 h-[2.5px] bg-brand-600 rounded-full shadow-[0_0_8px_rgba(var(--app-primary-rgb),0.5)] transition-all duration-300 group-hover:h-[3px]" />
                  )}

                  {/* Hover Underline (Fade-in for other items) */}
                  {!isActive && !item.isRevenue && (
                    <div className="absolute bottom-0 left-3 right-3 h-[2px] bg-gradient-to-r from-brand-400/40 to-brand-400/40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-[0_0_8px_rgba(var(--app-primary-rgb),0.2)]" />
                  )}

                  {/* Dropdowns */}
                  {item.isDropdown && item.dropdownId === 'services' && (
                    <AnimatePresence>
                      {activeDropdown === 'services' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full -left-48 pt-6 w-[800px] z-[100]"
                        >
                          <div className={`rounded-2xl shadow-2xl border overflow-hidden transition-colors ${isLight ? 'bg-white opacity-100 border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.15)] text-slate-900' : 'bg-[#08080c]/98 backdrop-blur-2xl opacity-100 border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.9)] text-white'}`}>
                            <div className="grid grid-cols-3 p-6 gap-8 text-left">
                              {/* Core Development */}
                              <div>
                                <h3 className={cn("text-sm font-bold mb-4 border-b pb-2", isLight ? "text-slate-900 border-slate-200" : "text-white border-white/10")}>Core Development</h3>
                                <div className="space-y-4">
                                  {servicesMenu.core.map((s) => (
                                    <Link key={s.name} to={s.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                        {s.icon}
                                      </div>
                                      <div>
                                        <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{s.name}</div>
                                        <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{s.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* Design & Marketing */}
                              <div>
                                <h3 className={cn("text-sm font-bold mb-4 border-b pb-2", isLight ? "text-slate-900 border-slate-200" : "text-white border-white/10")}>Design & Marketing</h3>
                                <div className="space-y-4">
                                  {servicesMenu.design.map((s) => (
                                    <Link key={s.name} to={s.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                        {s.icon}
                                      </div>
                                      <div>
                                        <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{s.name}</div>
                                        <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{s.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* E-commerce */}
                              <div>
                                <h3 className={cn("text-sm font-bold mb-4 border-b pb-2", isLight ? "text-slate-900 border-slate-200" : "text-white border-white/10")}>E-commerce</h3>
                                <div className="space-y-4">
                                  {servicesMenu.ecommerce.map((s) => (
                                    <Link key={s.name} to={s.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                        {s.icon}
                                      </div>
                                      <div>
                                        <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{s.name}</div>
                                        <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{s.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {/* Bottom Bar */}
                            <div className={`p-4 border-t flex justify-between items-center px-8 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-[#040810] border-white/10'}`}>
                              {servicesMenu.bottom.map((s) => (
                                <Link key={s.name} to={s.path} className="flex flex-col items-center gap-1 group/bottom transition-all duration-300 hover:-translate-y-1">
                                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${isLight ? 'bg-white border-slate-200 group-hover/bottom:border-brand-500' : 'bg-[#090F1E] border-white/10 group-hover/bottom:border-brand-500/50'}`}>
                                    {s.icon}
                                  </div>
                                  <span className={cn("text-xs font-medium group-hover/bottom:text-brand-400", isLight ? "text-slate-600" : "text-slate-400")}>{s.name}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {item.isDropdown && item.dropdownId === 'industries' && (
                    <AnimatePresence>
                      {activeDropdown === 'industries' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full -left-64 pt-6 w-[800px] z-[100]"
                        >
                          <div className={`rounded-2xl shadow-2xl border p-6 transition-colors ${isLight ? 'bg-white opacity-100 border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.15)] text-slate-900' : 'bg-[#08080c]/98 backdrop-blur-2xl opacity-100 border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.9)] text-white'}`}>
                            <div className="grid grid-cols-3 gap-4 text-left">
                              {industriesMenu.map((ind) => (
                                <Link key={ind.name} to={ind.path} className={cn("flex items-center gap-3 p-2 rounded-xl transition-all duration-300 hover:translate-x-1 group/item", isLight ? "hover:bg-slate-100" : "hover:bg-white/5")}>
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                    {ind.icon}
                                  </div>
                                  <div>
                                    <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{ind.name}</div>
                                    <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{ind.desc}</div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {item.isDropdown && item.dropdownId === 'tools' && (
                    <AnimatePresence>
                      {activeDropdown === 'tools' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full -left-72 lg:-left-[380px] xl:-left-[420px] pt-6 w-[800px] z-[100]"
                        >
                          <div
                            ref={megaMenuRef}
                            className={`relative rounded-2xl shadow-2xl border transition-colors ${isLight ? 'bg-white opacity-100 border-slate-200 shadow-[0_25px_60px_rgba(0,0,0,0.15)] text-slate-900' : 'bg-[#08080c]/98 backdrop-blur-2xl opacity-100 border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.9)] text-white'}`}
                          >
                            <div className="grid grid-cols-3 p-6 gap-8 text-left">
                              {/* Developer Tools */}
                              <div>
                                <h3 className={cn("text-sm font-bold mb-4 border-b pb-2", isLight ? "text-slate-900 border-slate-200" : "text-white border-white/10")}>Developer Tools</h3>
                                <div className="space-y-4">
                                  {toolsMenu.developer.map((t) => (
                                    <Link key={t.name} to={t.path} onClick={() => { setActiveDropdown(null); setIsMoreUtilityOpen(false); }} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                        {t.icon}
                                      </div>
                                      <div>
                                        <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{t.name}</div>
                                        <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{t.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* Image Tools */}
                              <div>
                                <h3 className={cn("text-sm font-bold mb-4 border-b pb-2", isLight ? "text-slate-900 border-slate-200" : "text-white border-white/10")}>Image Tools</h3>
                                <div className="space-y-4">
                                  {toolsMenu.image.map((t) => (
                                    <Link key={t.name} to={t.path} onClick={() => { setActiveDropdown(null); setIsMoreUtilityOpen(false); }} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                        {t.icon}
                                      </div>
                                      <div>
                                        <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{t.name}</div>
                                        <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{t.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* Utility Tools */}
                              <div>
                                <h3 className={cn("text-sm font-bold mb-4 border-b pb-2", isLight ? "text-slate-900 border-slate-200" : "text-white border-white/10")}>Utility Tools</h3>
                                <div className="space-y-4">
                                  {primaryUtilityTools.map((t) => (
                                    <Link key={t.name} to={t.path} onClick={() => { setActiveDropdown(null); setIsMoreUtilityOpen(false); }} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all ${isLight ? 'bg-slate-100 border-slate-200 shadow-sm group-hover/item:shadow-md' : 'bg-white/5 border-white/10 group-hover/item:border-brand-500/50'}`}>
                                        {t.icon}
                                      </div>
                                      <div>
                                        <div className={cn("text-sm font-bold transition-colors group-hover/item:text-brand-400", isLight ? "text-slate-900" : "text-slate-200")}>{t.name}</div>
                                        <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{t.desc}</div>
                                      </div>
                                    </Link>
                                  ))}

                                  {/* "More Utility Tools →" Trigger Item */}
                                  {moreUtilityTools.length > 0 && (
                                    <div className="pt-1">
                                      <button
                                        type="button"
                                        ref={moreUtilityTriggerRef}
                                        aria-haspopup="true"
                                        aria-expanded={isMoreUtilityOpen}
                                        aria-controls="more-utility-tools-submenu"
                                        onMouseEnter={handleMoreMouseEnter}
                                        onMouseLeave={handleMoreMouseLeave}
                                        onFocus={handleMoreMouseEnter}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          updateSubmenuPosition();
                                          setIsMoreUtilityOpen((prev) => !prev);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                                            e.preventDefault();
                                            updateSubmenuPosition();
                                            setIsMoreUtilityOpen(true);
                                            setTimeout(() => {
                                              const firstLink = submenuRef.current?.querySelector<HTMLAnchorElement>('a');
                                              firstLink?.focus();
                                            }, 60);
                                          } else if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setIsMoreUtilityOpen(false);
                                          }
                                        }}
                                        className={cn(
                                          "w-full flex items-center justify-between p-2.5 rounded-xl font-bold text-xs transition-all duration-200 cursor-pointer border group/more text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                                          isMoreUtilityOpen
                                            ? (isLight ? "bg-brand-50 text-brand-600 border-brand-200 shadow-sm" : "bg-white/10 text-brand-400 border-brand-500/40")
                                            : (isLight ? "bg-slate-50 text-slate-700 hover:text-brand-600 hover:bg-slate-100 border-slate-200/80" : "bg-white/[0.03] text-slate-300 hover:text-white hover:bg-white/5 border-white/5")
                                        )}
                                      >
                                        <div className="flex items-center gap-2.5">
                                          <div className={cn(
                                            "w-7 h-7 rounded-lg flex items-center justify-center border transition-all shrink-0",
                                            isLight ? "bg-white border-slate-200 text-brand-600 shadow-xs" : "bg-white/5 border-white/10 text-brand-400"
                                          )}>
                                            <span className="text-[11px] font-black">+{moreUtilityTools.length}</span>
                                          </div>
                                          <div>
                                            <div className="text-xs font-bold leading-tight">More Utility Tools</div>
                                            <div className={cn("text-[10px] font-normal leading-tight", isLight ? "text-slate-500" : "text-slate-400")}>
                                              View {moreUtilityTools.length} more tools
                                            </div>
                                          </div>
                                        </div>
                                        <ChevronRight className={cn(
                                          "w-4 h-4 transition-transform duration-200 shrink-0",
                                          isMoreUtilityOpen
                                            ? (submenuSide === 'left' ? "-translate-x-1 rotate-180 text-brand-600 dark:text-brand-400" : "translate-x-1 text-brand-600 dark:text-brand-400")
                                            : "text-slate-400 group-hover/more:translate-x-0.5"
                                        )} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Nested Submenu for More Utility Tools */}
                            <AnimatePresence>
                              {isMoreUtilityOpen && moreUtilityTools.length > 0 && (
                                <motion.div
                                  id="more-utility-tools-submenu"
                                  ref={submenuRef}
                                  role="region"
                                  aria-label="More Utility Tools Submenu"
                                  initial={{ opacity: 0, x: submenuSide === 'right' ? -12 : 12, scale: 0.98 }}
                                  animate={{ opacity: 1, x: 0, scale: 1 }}
                                  exit={{ opacity: 0, x: submenuSide === 'right' ? -12 : 12, scale: 0.98 }}
                                  transition={{ duration: 0.18, ease: "easeOut" }}
                                  onMouseEnter={handleMoreMouseEnter}
                                  onMouseLeave={handleMoreMouseLeave}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setIsMoreUtilityOpen(false);
                                      moreUtilityTriggerRef.current?.focus();
                                    } else if (
                                      (submenuSide === 'right' && e.key === 'ArrowLeft') ||
                                      (submenuSide === 'left' && e.key === 'ArrowRight')
                                    ) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setIsMoreUtilityOpen(false);
                                      moreUtilityTriggerRef.current?.focus();
                                    }
                                  }}
                                  className="absolute bottom-14 w-[280px] z-[150] pointer-events-auto"
                                  style={{
                                    left: submenuSide === 'right' ? 'calc(100% + 12px)' : undefined,
                                    right: submenuSide === 'left' ? 'calc(100% + 12px)' : undefined
                                  }}
                                >
                                  {/* Invisible Hover Bridges spanning the 12px gap preventing any gap flicker */}
                                  <div
                                    className="absolute top-0 bottom-0 w-6 pointer-events-auto"
                                    style={{
                                      right: submenuSide === 'right' ? '100%' : undefined,
                                      left: submenuSide === 'left' ? '100%' : undefined
                                    }}
                                    aria-hidden="true"
                                  />

                                  <div
                                    className={cn(
                                      "rounded-2xl border p-4 shadow-2xl transition-colors",
                                      isLight
                                        ? "bg-white border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.18)] text-slate-900"
                                        : "bg-[#08080c]/98 backdrop-blur-2xl border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.95)] text-white"
                                    )}
                                  >
                                    {/* Submenu Header */}
                                    <div className={cn("flex items-center justify-between pb-3 mb-3 border-b", isLight ? "border-slate-200 text-slate-900" : "border-white/10 text-white")}>
                                      <div className="flex items-center gap-2">
                                        <span className={cn("text-[11px] font-black uppercase tracking-wider", isLight ? "text-brand-600" : "text-brand-400")}>
                                          Utility Tools
                                        </span>
                                        <span className={cn(
                                          "text-[10px] font-extrabold px-2 py-0.5 rounded-full border",
                                          isLight ? "bg-brand-50 text-brand-700 border-brand-200" : "bg-brand-500/10 text-brand-400 border-brand-500/20"
                                        )}>
                                          {moreUtilityTools.length} additional
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setIsMoreUtilityOpen(false);
                                          moreUtilityTriggerRef.current?.focus();
                                        }}
                                        className={cn("p-1 rounded-md transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-white cursor-pointer")}
                                        aria-label="Close submenu"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>

                                    {/* Tools List */}
                                    <div className="space-y-3">
                                      {moreUtilityTools.map((t) => (
                                        <Link
                                          key={t.name}
                                          to={t.path}
                                          onClick={() => {
                                            setActiveDropdown(null);
                                            setIsMoreUtilityOpen(false);
                                          }}
                                          className={cn(
                                            "flex items-start gap-3 p-2 rounded-xl group/sub transition-all duration-300 hover:translate-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                                            isLight ? "hover:bg-slate-100" : "hover:bg-white/5"
                                          )}
                                        >
                                          <div
                                            className={cn(
                                              "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border transition-all",
                                              isLight
                                                ? "bg-slate-100 border-slate-200 shadow-sm group-hover/sub:shadow-md"
                                                : "bg-white/5 border-white/10 group-hover/sub:border-brand-500/50"
                                            )}
                                          >
                                            {t.icon}
                                          </div>
                                          <div className="min-w-0 flex-1">
                                            <div
                                              className={cn(
                                                "text-sm font-bold transition-colors group-hover/sub:text-brand-400 truncate",
                                                isLight ? "text-slate-900" : "text-slate-200"
                                              )}
                                            >
                                              {t.name}
                                            </div>
                                            <div className={cn("text-xs line-clamp-1", isLight ? "text-slate-500" : "text-slate-400")}>
                                              {t.desc}
                                            </div>
                                          </div>
                                        </Link>
                                      ))}
                                    </div>

                                    {/* Submenu Footer shortcut to all utilities */}
                                    <div className={cn("mt-4 pt-3 border-t flex justify-between items-center text-xs font-bold", isLight ? "border-slate-200" : "border-white/10")}>
                                      <Link
                                        to="/tools/utility"
                                        onClick={() => {
                                          setActiveDropdown(null);
                                          setIsMoreUtilityOpen(false);
                                        }}
                                        className={cn("hover:underline flex items-center gap-1", isLight ? "text-brand-600" : "text-brand-400")}
                                      >
                                        <span>All Utility Tools</span>
                                        <span>&rarr;</span>
                                      </Link>
                                      <span className={cn("text-[10px] font-normal", isLight ? "text-slate-400" : "text-slate-500")}>
                                        Press Esc to close
                                      </span>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Bottom Bar: SEO Tools & Hub Link */}
                            <div className={`p-4 border-t flex justify-between items-center px-6 rounded-b-2xl ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-[#040810] border-white/10'}`}>
                              <div className="flex items-center gap-3">
                                <span className={cn("text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border", isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20")}>
                                  New SEO Tool
                                </span>
                                <Link
                                  to="/tools/meta-tag-generator"
                                  onClick={() => setActiveDropdown(null)}
                                  className={cn("text-xs font-bold hover:underline flex items-center gap-1.5 transition-colors", isLight ? "text-slate-800 hover:text-emerald-600" : "text-slate-200 hover:text-emerald-400")}
                                >
                                  <Search className="w-3.5 h-3.5 text-emerald-500" />
                                  <span>Meta Tag Generator</span>
                                </Link>
                              </div>
                              <Link
                                to="/tools"
                                onClick={() => setActiveDropdown(null)}
                                className={cn("text-xs font-bold hover:underline flex items-center gap-1", isLight ? "text-brand-600" : "text-brand-400")}
                              >
                                <span>View All Tools</span>
                                <span>&rarr;</span>
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            {isAboutPage && (
              <style>{`
                .about-nav-quote-btn,
                .about-nav-quote-btn:hover,
                .about-nav-quote-btn:focus,
                .about-nav-quote-btn:active {
                  transform: none !important;
                }
              `}</style>
            )}
            <Link
              to="/contact-us"
              className={cn(
                "site-button-primary relative px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2 group",
                isAboutPage
                  ? "about-nav-quote-btn !transform-none hover:!transform-none focus:!transform-none active:!transform-none transition-[background,box-shadow,border-color] duration-300"
                  : "transition-all duration-500 hover:-translate-y-0.5"
              )}
            >
              <span className="relative z-10">Get a Quote</span>
              <ChevronRight className="relative z-10 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className={cn(
                "p-2.5 rounded-xl z-50 relative cursor-pointer transition-colors shadow-sm",
                isLight ? "bg-brand-600 text-white hover:bg-brand-700" : "bg-white/10 text-white hover:bg-white/20"
              )}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle Navigation Menu"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={cn(
              "lg:hidden absolute top-full left-0 w-full shadow-2xl border-t overflow-hidden z-[100] transition-colors",
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-[#08080c]/98 backdrop-blur-2xl border-white/10 text-white"
            )}
          >
            <div className="flex flex-col px-4 py-6 gap-1 max-h-[85vh] overflow-y-auto custom-scrollbar">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className={cn("text-lg font-bold p-4 rounded-2xl transition-all", isLight ? "text-slate-900 hover:bg-slate-100 hover:text-brand-600" : "text-slate-200 hover:bg-white/5 hover:text-brand-400")}
              >
                Home
              </Link>

              {/* Revenue link with New! badge and blue styling */}
              <a
                href="/revenue"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "mobile-revenue-link flex items-center justify-between p-4 rounded-2xl transition-all font-bold text-lg border my-1 shadow-sm group",
                  isLight
                    ? "bg-brand-50/90 border-brand-200/90 hover:bg-brand-100/80 !text-brand-800"
                    : "bg-brand-950/40 border-brand-500/30 hover:bg-brand-900/40 !text-brand-400"
                )}
                style={{ color: isLight ? 'var(--color-brand-800)' : 'var(--color-brand-400)' }}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className={cn(
                      "revenue-text font-extrabold text-lg tracking-tight",
                      isLight ? "!text-brand-800" : "!text-brand-400"
                    )}
                    style={{ color: isLight ? 'var(--color-brand-800)' : 'var(--color-brand-400)' }}
                  >
                    Revenue
                  </span>
                  <span
                    style={{ color: '#ffffff', background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}
                    className="px-2 py-0.5 rounded-full text-[9px] font-black !text-white shadow-[0_2px_8px_rgba(245,158,11,0.55)] tracking-wider uppercase leading-none border border-yellow-200/80 drop-shadow-sm"
                  >
                    NEW!
                  </span>
                </div>
                <ArrowRight
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  style={{ color: isLight ? 'var(--color-brand-800)' : 'var(--color-brand-400)', stroke: isLight ? 'var(--color-brand-800)' : 'var(--color-brand-400)' }}
                />
              </a>

              {/* Services Mobile Section */}
              <div className={cn("mt-2 border-b pb-2", isLight ? "border-slate-200" : "border-white/10")}>
                <div className="flex items-center justify-between w-full">
                  <Link
                    to="/services"
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "flex-1 p-4 text-lg font-bold transition-all text-left",
                      isLight
                        ? (location.pathname.startsWith('/services') ? "text-brand-600" : "text-slate-900 hover:text-brand-600")
                        : (location.pathname.startsWith('/services') ? "text-brand-400" : "text-slate-200 hover:text-brand-400")
                    )}
                  >
                    Services
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMobileSection('services');
                    }}
                    className={cn(
                      "p-4 flex items-center justify-center transition-colors cursor-pointer",
                      isLight ? "text-slate-500 hover:text-brand-600" : "text-slate-400 hover:text-brand-400"
                    )}
                    aria-label="Toggle services submenu"
                  >
                    <ChevronDown className={cn("w-5 h-5 transition-transform duration-300", expandedMobileSection === 'services' ? "rotate-180 text-brand-600 dark:text-brand-400" : "text-slate-400")} />
                  </button>
                </div>

                <AnimatePresence>
                  {expandedMobileSection === 'services' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className={cn("overflow-hidden rounded-2xl mx-2 transition-colors", isLight ? "bg-slate-50 border border-slate-200/80" : "bg-white/[0.03] border border-white/5")}
                    >
                      <div className="p-4 flex flex-col gap-6">
                        <div>
                          <h4 className={cn("text-[11px] font-black mb-3 uppercase tracking-widest pl-2", isLight ? "text-brand-600" : "text-brand-400")}>Core Development</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {servicesMenu.core.map(s => (
                              <Link key={s.name} to={s.path} onClick={() => setIsOpen(false)} className={cn("flex items-center gap-3 p-3 text-sm font-semibold rounded-xl transition-colors", isLight ? "text-slate-800 hover:bg-white hover:text-brand-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white")}>
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? "bg-brand-100" : "bg-white/5")}>{s.icon}</div>
                                <span>{s.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className={cn("text-[11px] font-black mb-3 uppercase tracking-widest pl-2", isLight ? "text-brand-600" : "text-brand-400")}>Design & Marketing</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {servicesMenu.design.map(s => (
                              <Link key={s.name} to={s.path} onClick={() => setIsOpen(false)} className={cn("flex items-center gap-3 p-3 text-sm font-semibold rounded-xl transition-colors", isLight ? "text-slate-800 hover:bg-white hover:text-brand-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white")}>
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? "bg-purple-100" : "bg-white/5")}>{s.icon}</div>
                                <span>{s.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Industries Mobile Section */}
              <div className={cn("mt-2 border-b pb-2", isLight ? "border-slate-200" : "border-white/10")}>
                <div className="flex items-center justify-between w-full">
                  <Link
                    to="/industries"
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "flex-1 p-4 text-lg font-bold transition-all text-left",
                      isLight
                        ? (location.pathname.startsWith('/industries') ? "text-brand-600" : "text-slate-900 hover:text-brand-600")
                        : (location.pathname.startsWith('/industries') ? "text-brand-400" : "text-slate-200 hover:text-brand-400")
                    )}
                  >
                    Industries
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMobileSection('industries');
                    }}
                    className={cn(
                      "p-4 flex items-center justify-center transition-colors cursor-pointer",
                      isLight ? "text-slate-500 hover:text-brand-600" : "text-slate-400 hover:text-brand-400"
                    )}
                    aria-label="Toggle industries submenu"
                  >
                    <ChevronDown className={cn("w-5 h-5 transition-transform duration-300", expandedMobileSection === 'industries' ? "rotate-180 text-brand-600 dark:text-brand-400" : "text-slate-400")} />
                  </button>
                </div>

                <AnimatePresence>
                  {expandedMobileSection === 'industries' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className={cn("overflow-hidden rounded-2xl mx-2 transition-colors", isLight ? "bg-slate-50 border border-slate-200/80" : "bg-white/[0.03] border border-white/5")}
                    >
                      <div className="p-4 grid grid-cols-2 gap-2">
                        {industriesMenu.slice(0, 8).map(ind => (
                          <Link key={ind.name} to={ind.path} onClick={() => setIsOpen(false)} className={cn("flex flex-col gap-2 p-3 rounded-xl border transition-all", isLight ? "bg-white border-slate-200 hover:border-brand-300 shadow-sm" : "bg-white/5 border-white/5 hover:border-brand-500/30")}>
                            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? ind.bgColor : "bg-white/5")}>{ind.icon}</div>
                            <span className={cn("text-[11px] font-bold", isLight ? "text-slate-800" : "text-slate-300")}>{ind.name}</span>
                          </Link>
                        ))}
                        <Link to="/industries" onClick={() => setIsOpen(false)} className={cn("col-span-2 text-center p-3 text-xs font-bold rounded-xl mt-2 transition-colors", isLight ? "text-brand-600 hover:bg-white shadow-sm" : "text-brand-400 hover:bg-white/5")}>View All Industries</Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Tools Mobile Section */}
              <div className={cn("mt-2 border-b pb-2", isLight ? "border-slate-200" : "border-white/10")}>
                <div className="flex items-center justify-between w-full">
                  <Link
                    to="/tools"
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "flex-1 p-4 text-lg font-bold transition-all text-left",
                      isLight
                        ? (location.pathname === '/tools' || location.pathname.startsWith('/tools/') ? "text-brand-600" : "text-slate-900 hover:text-brand-600")
                        : (location.pathname === '/tools' || location.pathname.startsWith('/tools/') ? "text-brand-400" : "text-slate-200 hover:text-brand-400")
                    )}
                  >
                    Tools
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMobileSection('tools');
                    }}
                    className={cn(
                      "p-4 flex items-center justify-center transition-colors cursor-pointer",
                      isLight ? "text-slate-500 hover:text-brand-600" : "text-slate-400 hover:text-brand-400"
                    )}
                    aria-label="Toggle tools submenu"
                  >
                    <ChevronDown className={cn("w-5 h-5 transition-transform duration-300", expandedMobileSection === 'tools' ? "rotate-180 text-brand-600 dark:text-brand-400" : "text-slate-400")} />
                  </button>
                </div>

                <AnimatePresence>
                  {expandedMobileSection === 'tools' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className={cn("overflow-hidden rounded-2xl mx-2 transition-colors", isLight ? "bg-slate-50 border border-slate-200/80" : "bg-white/[0.03] border border-white/5")}
                    >
                      <div className="p-4 flex flex-col gap-6">
                        {/* Developer Tools */}
                        <div>
                          <h4 className={cn("text-[11px] font-black mb-3 uppercase tracking-widest pl-2", isLight ? "text-brand-600" : "text-brand-400")}>Developer Tools</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {toolsMenu.developer.map(t => (
                              <Link key={t.name} to={t.path} onClick={() => setIsOpen(false)} className={cn("flex items-center gap-3 p-3 text-sm font-semibold rounded-xl transition-colors", isLight ? "text-slate-800 hover:bg-white hover:text-brand-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white")}>
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? "bg-brand-100" : "bg-white/5")}>{t.icon}</div>
                                <span>{t.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Image Tools */}
                        <div>
                          <h4 className={cn("text-[11px] font-black mb-3 uppercase tracking-widest pl-2", isLight ? "text-emerald-600" : "text-emerald-400")}>Image Tools</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {toolsMenu.image.map(t => (
                              <Link key={t.name} to={t.path} onClick={() => setIsOpen(false)} className={cn("flex items-center gap-3 p-3 text-sm font-semibold rounded-xl transition-colors", isLight ? "text-slate-800 hover:bg-white hover:text-emerald-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white")}>
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? "bg-emerald-100" : "bg-white/5")}>{t.icon}</div>
                                <span>{t.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Utility Tools */}
                        <div>
                          <div className="flex items-center justify-between mb-3 px-2">
                            <h4 className={cn("text-[11px] font-black uppercase tracking-widest", isLight ? "text-brand-600" : "text-brand-400")}>Utility Tools</h4>
                            <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full border", isLight ? "bg-brand-50 text-brand-700 border-brand-200" : "bg-brand-500/10 text-brand-400 border-brand-500/20")}>
                              {toolsMenu.utility.length} tools
                            </span>
                          </div>
                          <div className="grid grid-cols-1 gap-1">
                            {primaryUtilityTools.map(t => (
                              <Link key={t.name} to={t.path} onClick={() => setIsOpen(false)} className={cn("flex items-center gap-3 p-3 text-sm font-semibold rounded-xl transition-colors", isLight ? "text-slate-800 hover:bg-white hover:text-brand-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white")}>
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? "bg-brand-100" : "bg-white/5")}>{t.icon}</div>
                                <span>{t.name}</span>
                              </Link>
                            ))}
                          </div>

                          {/* Mobile More Utility Tools Accordion */}
                          {moreUtilityTools.length > 0 && (
                            <div className="mt-2">
                              <button
                                type="button"
                                onClick={() => setExpandedMobileMoreUtility(!expandedMobileMoreUtility)}
                                className={cn(
                                  "w-full flex items-center justify-between p-3 rounded-xl text-sm font-bold transition-all border cursor-pointer",
                                  expandedMobileMoreUtility
                                    ? (isLight ? "bg-brand-50 text-brand-600 border-brand-200 shadow-xs" : "bg-white/10 text-brand-400 border-brand-500/30")
                                    : (isLight ? "bg-white text-slate-700 border-slate-200 hover:text-brand-600 shadow-sm" : "bg-white/5 text-slate-300 border-white/5 hover:text-white")
                                )}
                                aria-expanded={expandedMobileMoreUtility}
                              >
                                <div className="flex items-center gap-2.5">
                                  <span className={cn(
                                    "w-6 h-6 rounded-md flex items-center justify-center text-xs font-black border",
                                    isLight ? "bg-brand-100 text-brand-700 border-brand-200" : "bg-brand-500/20 text-brand-400 border-brand-500/30"
                                  )}>
                                    +{moreUtilityTools.length}
                                  </span>
                                  <span>More Utility Tools</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs">
                                  <span className={cn("text-xs font-normal", isLight ? "text-slate-500" : "text-slate-400")}>
                                    {expandedMobileMoreUtility ? "Hide" : "Show"}
                                  </span>
                                  <ChevronDown className={cn(
                                    "w-4 h-4 transition-transform duration-300",
                                    expandedMobileMoreUtility ? "rotate-180 text-brand-600 dark:text-brand-400" : "text-slate-400"
                                  )} />
                                </div>
                              </button>

                              <AnimatePresence>
                                {expandedMobileMoreUtility && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="pt-2 pl-2 flex flex-col gap-1 border-l-2 ml-3 my-1 border-brand-500/30">
                                      {moreUtilityTools.map((t) => (
                                        <Link
                                          key={t.name}
                                          to={t.path}
                                          onClick={() => setIsOpen(false)}
                                          className={cn(
                                            "flex items-center gap-3 p-2.5 text-sm font-semibold rounded-xl transition-colors",
                                            isLight ? "text-slate-800 hover:bg-white hover:text-brand-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white"
                                          )}
                                        >
                                          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", isLight ? "bg-brand-100" : "bg-white/5")}>
                                            {t.icon}
                                          </div>
                                          <div>
                                            <div className="text-sm font-bold">{t.name}</div>
                                            <div className={cn("text-xs", isLight ? "text-slate-500" : "text-slate-400")}>{t.desc}</div>
                                          </div>
                                        </Link>
                                      ))}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </div>

                        {/* SEO Tools */}
                        <div>
                          <h4 className={cn("text-[11px] font-black mb-3 uppercase tracking-widest pl-2", isLight ? "text-emerald-600" : "text-emerald-400")}>SEO Tools</h4>
                          <div className="grid grid-cols-1 gap-1">
                            <Link
                              to="/tools/meta-tag-generator"
                              onClick={() => setIsOpen(false)}
                              className={cn("flex items-center gap-3 p-3 text-sm font-semibold rounded-xl transition-colors", isLight ? "text-slate-800 hover:bg-white hover:text-emerald-600 shadow-sm" : "text-slate-200 hover:bg-white/5 hover:text-white")}
                            >
                              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", isLight ? "bg-emerald-100" : "bg-white/5")}>
                                <Search className="w-4 h-4 text-emerald-500" />
                              </div>
                              <span>Meta Tag Generator</span>
                            </Link>
                          </div>
                        </div>

                        <Link
                          to="/tools"
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "text-center p-3 text-xs font-bold rounded-xl mt-2 transition-colors block border",
                            isLight
                              ? "bg-white border-brand-200 text-brand-600 hover:bg-brand-50 shadow-sm"
                              : "bg-white/5 border-white/10 text-brand-400 hover:bg-white/10"
                          )}
                        >
                          View All Free Tools &rarr;
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/portfolio"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "text-lg font-bold p-4 rounded-2xl transition-all",
                  isLight
                    ? (location.pathname.startsWith('/portfolio') ? "text-brand-600 bg-brand-50" : "text-slate-900 hover:bg-slate-100 hover:text-brand-600")
                    : (location.pathname.startsWith('/portfolio') ? "text-brand-400 bg-white/5" : "text-slate-200 hover:bg-white/5 hover:text-brand-400")
                )}
              >
                Portfolio
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "text-lg font-bold p-4 rounded-2xl transition-all",
                  isLight
                    ? (location.pathname.startsWith('/blog') ? "text-brand-600 bg-brand-50" : "text-slate-900 hover:bg-slate-100 hover:text-brand-600")
                    : (location.pathname.startsWith('/blog') ? "text-brand-400 bg-white/5" : "text-slate-200 hover:bg-white/5 hover:text-brand-400")
                )}
              >
                Blog
              </Link>
              <Link
                to="/about-us"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "text-lg font-bold p-4 rounded-2xl transition-all",
                  isLight
                    ? (location.pathname.startsWith('/about-us') ? "text-brand-600 bg-brand-50" : "text-slate-900 hover:bg-slate-100 hover:text-brand-600")
                    : (location.pathname.startsWith('/about-us') ? "text-brand-400 bg-white/5" : "text-slate-200 hover:bg-white/5 hover:text-brand-400")
                )}
              >
                About
              </Link>

              <Link
                to="/contact-us"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "px-6 py-4 rounded-full text-center font-bold mt-6 shadow-lg flex items-center justify-center gap-2 text-lg transition-all",
                  isLight
                    ? "bg-brand-600 hover:bg-brand-700 text-white shadow-brand-500/25"
                    : "bg-brand-500 hover:bg-brand-400 text-slate-950 shadow-[0_0_20px_rgba(var(--app-primary-rgb),0.3)]"
                )}
              >
                <span>Get a Quote</span>
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    </>
  );
}
